// Courage home: one card per area, each opening that area's home, with what's waiting on the person there. The area
// homes hold the apps and the Business Central pages; this page only gets you to them.
// (The helpers below — an area's apps, functions and Business Central pages — are used by the area homes.)
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "../icons";
import { bcUrl } from "../config";
import { areaHref, type Domain } from "../suite";
import { useLoad } from "../hooks";
import { myWork, waitingByArea } from "../mywork/myWorkApi";
import { AreaGrid } from "../SuiteGrid";
import { hasLanded, markLanded, startArea, useAccess } from "../security/store";

export type App = { name: string; to: string; note: string };
export type BcLinkItem = { name: string; page: number };

/** An area's apps: its navigation pages inside Courage, then live modules not already there, one per path. */
export function appsOf(d: Domain): App[] {
  const out: App[] = [];
  const seen = new Set<string>();
  const summary = (to: string) => d.modules.find((m) => m.path === to)?.summary ?? "";
  for (const p of d.nav ?? []) {
    if (!p.to || p.to.startsWith("/a/") || seen.has(p.to)) continue;
    seen.add(p.to);
    out.push({ name: p.name, to: p.to, note: p.note ?? summary(p.to) });
  }
  for (const m of d.modules) {
    if (m.status !== "live" || !m.path || seen.has(m.path)) continue;
    seen.add(m.path);
    out.push({ name: m.name, to: m.path, note: m.summary });
  }
  return out;
}

/** An area's Business Central pages: its navigation's BC links, then the pages of its BC-only modules. */
export function bcOf(d: Domain, max = 8): BcLinkItem[] {
  const out: BcLinkItem[] = [];
  const add = (name: string, page?: number) => { if (page && !out.some((x) => x.page === page)) out.push({ name, page }); };
  for (const p of d.nav ?? []) add(p.name, p.bc);
  for (const m of d.modules) if (m.status === "bc") for (const p of m.pages ?? []) add(p.name, p.bc);
  return out.slice(0, max);
}

export type Fn = { name: string; apps: App[]; bc: BcLinkItem[] };
/** An area's functions (its modules: Accounts, Planning, Purchasing, Warehouse…), each with its courageMFG apps and
 *  its Business Central pages. Apps that belong to no module become a function of their own. */
export function functionsOf(d: Domain): Fn[] {
  const apps = appsOf(d);
  const placed = new Set<string>();
  const usedBc = new Set<number>();
  const fns: Fn[] = [];
  const base = (to: string) => to.split("?")[0];
  for (const m of d.modules) {
    if (m.status !== "live" && m.status !== "bc") continue;
    const paths = new Set([m.path, ...(m.pages ?? []).map((p) => p.to)].filter(Boolean).map((x) => base(x!)));
    const mine = apps.filter((a) => !placed.has(a.to) && paths.has(base(a.to)));
    mine.forEach((a) => placed.add(a.to));
    const bc = (m.pages ?? []).filter((p) => p.bc && !usedBc.has(p.bc)).map((p) => ({ name: p.name, page: p.bc! }));
    bc.forEach((b) => usedBc.add(b.page));
    if (mine.length || bc.length) fns.push({ name: m.name, apps: mine, bc });
  }
  for (const a of apps) if (!placed.has(a.to)) fns.push({ name: a.name, apps: [a], bc: [] });
  const navBc = (d.nav ?? []).filter((p) => p.bc && !usedBc.has(p.bc)).map((p) => ({ name: p.name, page: p.bc! }));
  if (navBc.length) fns.push({ name: "More in Business Central", apps: [], bc: navBc });
  return fns;
}

/** Data (master data, history, lookups) or a transaction (documents, journals and worksheets you work in). */
export function bcKind(name: string): "data" | "transaction" {
  return /ledger entries|posted|chart of accounts|bank accounts|^customers|^contacts|^vendors|^items$|work centers|machine centers|boms|routings|^bins|lot numbers|item tracing|tooling/i.test(name) ? "data" : "transaction";
}

/** An area's Business Central pages as tiles in their own collapsible section, split into transactions and data,
 *  each tile captioned with its function. Open or closed is remembered per area and place. */
export function BcSection({ id, fns, compact = false, defaultOpen, forceOpen = false }: { id: string; fns: Fn[]; compact?: boolean; defaultOpen: boolean; forceOpen?: boolean }) {
  const key = `bc.open.${compact ? "home" : "area"}.${id}`;
  const [open, setOpen] = useState(() => { try { const v = localStorage.getItem(key); return v === null ? defaultOpen : v === "1"; } catch { return defaultOpen; } });
  const toggle = () => { setOpen((o) => { try { localStorage.setItem(key, o ? "0" : "1"); } catch { /* private window */ } return !o; }); };
  const tiles = fns.flatMap((f) => f.bc.map((l) => ({ ...l, fn: f.name })));
  if (!tiles.length) return null;
  const parts = [
    { kind: "transaction", title: "Transactions", hint: "Documents, journals and worksheets", items: tiles.filter((t) => bcKind(t.name) === "transaction") },
    { kind: "data", title: "Data", hint: "Master data, history and lookups", items: tiles.filter((t) => bcKind(t.name) === "data") },
  ].filter((p) => p.items.length);
  const shown = open || forceOpen;
  return (
    <div className={`bc-sec ${compact ? "compact" : ""} ${shown ? "open" : ""}`}>
      <button type="button" className="bc-sec-h" aria-expanded={shown} onClick={toggle}>
        <span className="chev" aria-hidden="true"><Icon name="chev" size={12} /></span>
        <span>Business Central</span>
        <span className="bc-sec-c">{parts.map((p) => `${p.items.length} ${p.title.toLowerCase()}`).join(" · ")}</span>
        {!compact && <span className="bc-sec-n"><Icon name="ext" size={11} />Opens in a new tab</span>}
      </button>
      {shown && (
        <div className="bc-parts">
          {parts.map((p) => (
            <div key={p.kind} className="bc-grp">
              <h4>{p.title}{!compact && <span>{p.hint}</span>}</h4>
              <div className="bc-grid">
                {p.items.map((l) => (
                  <a key={l.page} href={bcUrl(l.page)} target="_blank" rel="noopener noreferrer" className="bc-tile" title={`${l.name} opens in Business Central`}>
                    <span className="t"><span className="n">{l.name}</span><span className="f">{l.fn}</span></span>
                    <span className="x" aria-hidden="true"><Icon name="ext" size={13} /></span>
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Home({ name }: { name: string }) {
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const { data: work } = useLoad(() => myWork.count().catch(() => null));

  // Opening courageMFG lands on the person's own area home (Sales → CRM); people who span areas stay here. Once a
  // visit: after that, the logo comes back here. Waits for their access (a second at most) so this page doesn't flash.
  const access = useAccess();
  const go = useNavigate();
  const [landing, setLanding] = useState(() => !hasLanded());
  useEffect(() => {
    if (!landing) return;
    const t = window.setTimeout(() => { markLanded(); setLanding(false); }, 1500);
    return () => window.clearTimeout(t);
  }, [landing]);
  useEffect(() => {
    if (!landing || !access) return;
    markLanded();
    const d = startArea(access);
    if (d) go(areaHref(d), { replace: true });
    else setLanding(false);
  }, [landing, access]); // eslint-disable-line react-hooks/exhaustive-deps
  if (landing) return <div className="page" aria-busy="true" />;

  return (
    <div className="page apps-home">
      <div className="apps-head">
        <div>
          <div className="muted">{greet}, {name.split(" ")[0]}
            {work && work.waiting + work.overdue > 0 && (
              <Link to="/my-work" className="mw-home-l">
                {work.waiting > 0 && <><b>{work.waiting}</b> waiting on you</>}{work.waiting > 0 && work.overdue > 0 && ", "}{work.overdue > 0 && <><b>{work.overdue}</b> overdue</>}
              </Link>
            )}
          </div>
          <h1>Apps</h1>
        </div>
      </div>
      <AreaGrid home waiting={waitingByArea(work ?? null)} first={startArea(access)?.id ?? null} />
    </div>
  );
}
